import { useCallback, type ReactElement } from "react";
import type { StampDto, TopicLevel } from "@sikilesi/protocol";
import { useStamp } from "../hooks/useStamp";
import { editionUrl } from "./Header";
import { formatCount } from "../lib/time";
import { LEVEL_ORDER, levelLabel, SIKILESI, TAMSIKILESI, up } from "../lib/tr";
import { Dialog } from "./Dialog";
import { useTheme } from "./ThemeContext";
import { TurnstileBox } from "./TurnstileBox";

export interface StampButtonProps {
  topicId: string;
  title: string;
  stampCount: number;
  level: TopicLevel;
  onStamped?: (s: StampDto) => void;
  compact?: boolean;
}

/**
 * Level gauge: hafif → sıkılası → sikilesi → TAM. Not a picker: the level
 * climbs by itself as stamps pile up, so it is drawn as one filling bar.
 */
export function LevelMeter({ level }: { level: TopicLevel }): ReactElement {
  const reached = LEVEL_ORDER.indexOf(level);
  return (
    <div className="z-meter-wrap">
      <ol className="z-meter" aria-label={`Seviye: ${levelLabel(level)}. Damgalar biriktikçe kendiliğinden yükselir.`}>
        {LEVEL_ORDER.map((l, i) => (
          <li
            key={l}
            className={`${i <= reached ? "is-on" : ""}${i === reached ? " is-current" : ""}`.trim() || undefined}
            aria-current={i === reached ? "step" : undefined}
          >
            {levelLabel(l)}
          </li>
        ))}
      </ol>
      <span className="z-meter__hint" aria-hidden="true">{up("damga biriktikçe yükselir")}</span>
    </div>
  );
}

export function StampButton({ topicId, title, stampCount, level, onStamped, compact = false }: StampButtonProps): ReactElement {
  const flow = useStamp(topicId, onStamped);
  const theme = useTheme();
  // Live props (SSE) and our own stamp result: show whichever is further along.
  const count = Math.max(flow.result?.stamp_count ?? 0, stampCount);
  const resultLevel = flow.result?.level ?? level;
  const current = LEVEL_ORDER.indexOf(resultLevel) > LEVEL_ORDER.indexOf(level) ? resultLevel : level;
  const promoted = flow.result?.level === "tam" && level !== "tam";
  const { cancel: close, submit: send } = flow;
  const submit = useCallback(
    (token: string) => {
      void send(token);
    },
    [send],
  );
  const busy = flow.phase === "verifying" || flow.phase === "sending";

  return (
    <div className={compact ? "z-stamp z-stamp--compact" : "z-stamp"}>
      <button
        type="button"
        className={`z-stamp__btn${flow.stamped ? " is-stamped" : ""}`}
        onClick={flow.begin}
        disabled={flow.stamped || busy}
        aria-label={`${SIKILESI} damgası vur: ${title}. Şu an ${formatCount(count)} damga, seviye ${levelLabel(current)}.`}
        aria-pressed={flow.stamped}
      >
        <span className="z-stamp__word">{theme === "tam" ? TAMSIKILESI : levelLabel(current)}!</span>
        <span className="z-stamp__count">{formatCount(count)} DAMGA</span>
      </button>
      {!compact && <LevelMeter level={current} />}
      <div aria-live="polite" className="z-stamp__status">
        {flow.phase === "sending" && <span className="z-small">{up("damgalanıyor")}…</span>}
        {flow.phase === "done" && !flow.error && <span className="z-small">{up("damgalandı")}!</span>}
        {flow.error && (
          <span className="z-small" role={flow.phase === "error" ? "alert" : undefined}>
            {flow.error}
          </span>
        )}
        {promoted && (
          <a className="z-promo" href={editionUrl("tam")}>
            {theme === "tam" ? up("tarihe geçti") : `${up("tarihe geçti")} → tam.sikile.si`}
          </a>
        )}
      </div>
      <Dialog open={flow.phase === "verifying"} title={`${SIKILESI} damgası`} onClose={close}>
        <p className="z-mono">“{title}”</p>
        <TurnstileBox onToken={submit} />
      </Dialog>
    </div>
  );
}
